import router from '@/router'
import {EventBus} from '@/handler/event-bus'

// getSession() se llama cientos de veces por render (ej. una vez por mensaje
// en una lista de chat) y antes volvía a leer+parsear localStorage en cada
// llamada — con una sesión grande (muchos permisos/módulos) eso se vuelve
// carísimo y se nota específicamente en listas largas (ver profile de CPU:
// getSession sola se comía ~19s de un tipeo de unos pocos segundos).
// Se cachea en memoria y se invalida solo en los métodos de este mismo
// archivo que escriben esas claves (verificado: nada fuera de acá escribe
// _auth/_settings/_last_page/_parameters), más un listener de 'storage'
// para el caso de dos pestañas abiertas a la vez.
let cachedSession = null
const VIEW_METADATA_SCHEMA_VERSION = '1'
const VIEW_METADATA_VERSION_SUFFIX = '_view_metadata_version'

// Despliegue gradual de Mi espacio: P81 solo habilita la posibilidad de
// elegirlo. Para cambiar el comportamiento de usuarios sin preferencia
// guardada, modificar únicamente esta constante a 'modern'.
const DEFAULT_MY_SPACE_MODE = 'legacy'

function invalidateSessionCache() {
  cachedSession = null
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (!event.key || event.key.indexOf(window.location.origin) === 0) {
      invalidateSessionCache()
    }
  })
}

const session = {
  getSession(){
    if (cachedSession) return cachedSession

    var settings = localStorage.getItem(window.location.origin + '_settings')
    var auth = localStorage.getItem(window.location.origin + '_auth');
    var lastPage = localStorage.getItem(window.location.origin + '_last_page');
    var parameters = localStorage.getItem(window.location.origin + '_parameters');
    var viewMetadata = localStorage.getItem(window.location.origin + '_view_metadata');

    if(settings==undefined) {
      settings = null
    } else {
      settings = JSON.parse(settings)
    }
    if(auth==undefined) {auth = null} else {auth = JSON.parse(auth)}
    if(lastPage==undefined) {lastPage = null}
    if(parameters==undefined) {parameters = null} else {parameters = JSON.parse(parameters)}
    if(viewMetadata==undefined) {viewMetadata = {}} else {viewMetadata = JSON.parse(viewMetadata)}

    cachedSession = {  settings: settings,
              auth: auth,
              lastPage: lastPage,
              parameters: parameters,
              viewMetadata: viewMetadata,}
    return cachedSession
  },
  getToken(){
    // El token real ya no viaja por acá: axiosAuthInterceptor.js lo manda
    // por header Authorization en cada request. Se mantiene esta función
    // (y no se la vacía del todo) porque medio centenar de llamadores la
    // usan también como chequeo de "hay sesión" (`if (!Session.getToken())`),
    // y un string vacío es falsy en JS. Devuelve un query string vacío pero
    // truthy — sigue siendo seguro concatenarlo a cualquier URL existente.
    if(this.getSession().auth) {
      return "?"
    } else {
      this.removeSessionAuth()
      return ""
    }
  },
  getSessionAuth() {
    var auth = localStorage.getItem(window.location.origin + '_auth');

    if(auth==undefined) {auth = null} else {auth = JSON.parse(auth)}

    return auth
  },
  isAuthTokenExpired() {
    var auth = this.getSession().auth
    if(!auth || !auth.token) return false

    // El backend sigue siendo la autoridad para tokens revocados o inválidos
    // (eso se resuelve por el handler de errores). Este chequeo local cubre
    // el caso puntual de una SPA abierta mientras vence el JWT, sin sumar
    // requests periódicos al API.
    try {
      var payload = auth.token.split('.')[1]
      if(!payload) return false

      var base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
      var json = decodeURIComponent(atob(base64).split('').map(character => {
        return '%' + ('00' + character.charCodeAt(0).toString(16)).slice(-2)
      }).join(''))
      var expiration = JSON.parse(json).exp

      return typeof expiration == 'number' && Date.now() >= expiration * 1000
    } catch (error) {
      // Si no se puede decodificar, no asumir expiración: el API devolverá
      // token_error y seguirá usando el flujo actual de Error/ErrorToken.
      return false
    }
  },
  checkAuthTokenExpiration() {
    if(this.isAuthTokenExpired()) {
      this.removeSessionAuthTokenProblem()
      return true
    }
    return false
  },
  setSessionSettings(response){
    localStorage.removeItem(window.location.origin + '_settings')

    const values = {}
    response.settings.forEach(setting => {
      values[setting.reference] = setting.value
    })

    const requiredSettings = [
      'logo',
      'favicon',
      'image_login',
      'proyect_name',
    ]
    const statusSettings = requiredSettings.every(reference => values[reference])

    const data = {
      status: statusSettings,
      logo: values.logo || '',
      favicon: values.favicon || '',
      image_login: values.image_login || '',
      logo_contrast: values.logo_contrast || 'auto',
      proyect_name: values.proyect_name || '',
      image_watermark: values.image_watermark || '',
      branding: response.branding || {},
    }

    localStorage.setItem(window.location.origin + '_settings', JSON.stringify(data))
    invalidateSessionCache()
    EventBus.$emit('Settings:settings', true)
  },
  setSessionAuth(data){
    localStorage.removeItem(window.location.origin + '_auth')
    localStorage.setItem(window.location.origin + '_auth', JSON.stringify(data))
    this.setSessionViewMetadata(data)
    invalidateSessionCache()
    EventBus.$emit('Auth:auth', true)
  },
  setSessionViewMetadata(data) {
    const items = data && data.user && data.user.permissions && Array.isArray(data.user.permissions.items)
      ? data.user.permissions.items
      : []
    const viewMetadata = {}

    items.forEach((item) => {
      const key = [item.module_id, item.profile_id, item.reference].join(':')
      viewMetadata[key] = {
        module_id: item.module_id,
        profile_id: item.profile_id,
        reference: item.reference,
        title: item.title || '',
        subtitle: item.subtitle || '',
        icon: item.icon || '',
        mobile_first: !!item.mobile_first,
      }
    })

    localStorage.setItem(window.location.origin + '_view_metadata', JSON.stringify(viewMetadata))

    // No se marca como actualizada una sesión recibida antes de ejecutar la
    // migración/sincronización del backend. Así el contenedor vuelve a pedirla
    // en el próximo montaje en vez de conservar encabezados vacíos.
    const metadataReady = items.length === 0 || items.every(item => {
      return typeof item.title === 'string' && item.title.trim().length > 0
    })
    const versionKey = window.location.origin + VIEW_METADATA_VERSION_SUFFIX
    if (metadataReady) {
      localStorage.setItem(versionKey, VIEW_METADATA_SCHEMA_VERSION)
    } else {
      localStorage.removeItem(versionKey)
    }
    invalidateSessionCache()
    EventBus.$emit('ViewMetadata:updated')
  },
  needsViewMetadataRefresh() {
    const auth = this.getSession().auth
    if (!auth) return false

    const currentVersion = localStorage.getItem(
      window.location.origin + VIEW_METADATA_VERSION_SUFFIX
    )

    return currentVersion !== VIEW_METADATA_SCHEMA_VERSION
  },
  getViewMetadata(moduleId, profileId, reference) {
    const key = [moduleId, profileId, reference].join(':')
    return this.getSession().viewMetadata[key] || null
  },
  // A diferencia de getViewMetadata() (necesita module_id+profile_id+reference,
  // los tres), esta busca solo por reference — para casos como
  // DefaultTabsLayout.vue, que solo conoce el nombre de ruta actual y no
  // tiene forma simple de resolver module_id/profile_id desde ahí. reference
  // ya funciona como clave única en la práctica (es lo mismo que
  // access.view_reference en cada componente), así que un match alcanza.
  getViewMetadataByReference(reference) {
    const viewMetadata = this.getSession().viewMetadata
    const key = Object.keys(viewMetadata).find(k => viewMetadata[k].reference === reference)
    return key ? viewMetadata[key] : null
  },
  setSessionLastPage(data) {
    localStorage.removeItem(window.location.origin + '_last_page')
    localStorage.setItem(window.location.origin + '_last_page', JSON.stringify(data))
    invalidateSessionCache()
  },
  setSessionParameters(data){
    localStorage.setItem(window.location.origin + '_parameters_source', JSON.stringify(data))
    data = this.applyMySpaceModePreference(data)
    localStorage.removeItem(window.location.origin + '_parameters')
    localStorage.setItem(window.location.origin + '_parameters', JSON.stringify(data))
    invalidateSessionCache()
    EventBus.$emit('Parameters:parameters', true)
  },
  mySpaceModePreferenceKey() {
    var auth = this.getSession().auth
    if(!auth || !auth.user) return null
    return window.location.origin + '_my_space_mode_' + auth.user.id + '_' + auth.user.profile_id
  },
  getMySpaceModePreference() {
    var key = this.mySpaceModePreferenceKey()
    var value = key ? localStorage.getItem(key) : null
    return value === 'legacy' || value === 'modern' ? value : DEFAULT_MY_SPACE_MODE
  },
  hasMySpaceModeAvailable() {
    var source = localStorage.getItem(window.location.origin + '_parameters_source')
    var parameters = source ? JSON.parse(source) : this.getSession().parameters
    return Array.isArray(parameters) && parameters.some(item => Number(item.id) === 81)
  },
  isMySpaceModeEnabled() {
    return this.hasMySpaceModeAvailable() && this.getMySpaceModePreference() === 'modern'
  },
  applyMySpaceModePreference(data) {
    if(!Array.isArray(data) || this.getMySpaceModePreference() !== 'legacy') return data
    return data.filter(item => Number(item.id) !== 81)
  },
  setMySpaceModePreference(value) {
    var key = this.mySpaceModePreferenceKey()
    if(!key) return

    localStorage.setItem(key, value)
    var source = localStorage.getItem(window.location.origin + '_parameters_source')
    var parameters = source ? JSON.parse(source) : this.getSession().parameters
    this.setSessionParameters(Array.isArray(parameters) ? parameters : [])
  },
  removeSessionAuth(){
    localStorage.removeItem(window.location.origin + '_auth')
    localStorage.removeItem(window.location.origin + '_view_metadata')
    localStorage.removeItem(window.location.origin + VIEW_METADATA_VERSION_SUFFIX)
    invalidateSessionCache()
    router.push({ name: 'Init' });
  },
  removeSessionAuthTokenProblem(){
    this.setSessionLastPage(window.location.hash)
    localStorage.removeItem(window.location.origin + '_auth')
    localStorage.removeItem(window.location.origin + '_view_metadata')
    localStorage.removeItem(window.location.origin + VIEW_METADATA_VERSION_SUFFIX)
    invalidateSessionCache()
    router.push({ name: 'Init' });
  },
  removeSessionLastPage(){
    localStorage.removeItem(window.location.origin + '_last_page')
    invalidateSessionCache()
  },
  removeSessionParameters(){
    localStorage.removeItem(window.location.origin + '_parameters')
    localStorage.removeItem(window.location.origin + '_parameters_source')
    invalidateSessionCache()
  },
  removeSessionAll(){
    const preservedLocalStorage = {
      offl_queue: localStorage.getItem('offl_queue'),
    }

    localStorage.clear()
    sessionStorage.clear()
    invalidateSessionCache()

    Object.keys(preservedLocalStorage).forEach(key => {
      if (preservedLocalStorage[key] !== null) {
        localStorage.setItem(key, preservedLocalStorage[key])
      }
    })

    router.push({ name: 'Init' });
  },
}

export default session;
